'use client';

import React, { useState } from 'react';
import { AlertTriangle, TrendingUp, DollarSign, Lock, Settings, X, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { formatUGX } from './procurementData';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BudgetLine {
  budgetCode: string;
  budgetName: string;
  department: string;
  totalBudget: number;
  committed: number;   // Approved requisitions / POs not yet paid
  spent: number;       // Paid / fully delivered
  currency: string;
}

export interface BudgetThresholdRule {
  id: string;
  name: string;
  warningPercent: number;   // e.g. 80 → warn at 80% utilisation
  blockPercent: number;     // e.g. 100 → block at 100%
  enforced: boolean;
}

export interface BudgetTrackerProps {
  budgetCode: string;
  requestAmount: number;
  currency?: string;
  compact?: boolean;
  showSettings?: boolean;
}

// ─── Mock Budget Data ─────────────────────────────────────────────────────────

export const MOCK_BUDGETS: BudgetLine[] = [
  {
    budgetCode: 'BC-FEED-2026',
    budgetName: 'Feed Procurement Budget 2026',
    department: 'Feed Management',
    totalBudget: 120000000,
    committed: 45000000,
    spent: 62000000,
    currency: 'UGX',
  },
  {
    budgetCode: 'BC-HEALTH-2026',
    budgetName: 'Health & Veterinary Budget 2026',
    department: 'Health & Veterinary',
    totalBudget: 18000000,
    committed: 3600000,
    spent: 9800000,
    currency: 'UGX',
  },
  {
    budgetCode: 'BC-OPS-2026',
    budgetName: 'Farm Operations Budget 2026',
    department: 'Farm Operations',
    totalBudget: 15000000,
    committed: 1807500,
    spent: 8200000,
    currency: 'UGX',
  },
  {
    budgetCode: 'BC-ADMIN-2026',
    budgetName: 'Administration Budget 2026',
    department: 'Administration',
    totalBudget: 5000000,
    committed: 450000,
    spent: 3100000,
    currency: 'UGX',
  },
  {
    budgetCode: 'BC-MAINT-2026',
    budgetName: 'Maintenance Budget 2026',
    department: 'Maintenance',
    totalBudget: 12000000,
    committed: 2800000,
    spent: 7500000,
    currency: 'UGX',
  },
];

// ─── Default Threshold Rules ──────────────────────────────────────────────────

const DEFAULT_RULES: BudgetThresholdRule[] = [
  { id: 'rule-1', name: 'Warning Threshold', warningPercent: 80, blockPercent: 100, enforced: true },
];

// ─── Threshold Settings Modal ─────────────────────────────────────────────────

function ThresholdSettingsModal({
  rules,
  onSave,
  onClose,
}: {
  rules: BudgetThresholdRule[];
  onSave: (rules: BudgetThresholdRule[]) => void;
  onClose: () => void;
}) {
  const [localRules, setLocalRules] = useState<BudgetThresholdRule[]>(rules);

  function updateRule(id: string, field: keyof BudgetThresholdRule, value: number | boolean) {
    setLocalRules(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Budget Threshold Rules</h3>
            <p className="text-xs text-gray-400 mt-0.5">Configure warning and enforcement thresholds</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={15} className="text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {localRules.map(rule => (
            <div key={rule.id} className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-800">{rule.name}</span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-xs text-gray-500">Enforce</span>
                  <div
                    onClick={() => updateRule(rule.id, 'enforced', !rule.enforced)}
                    className={`w-9 h-5 rounded-full transition-colors cursor-pointer relative ${rule.enforced ? 'bg-orange-500' : 'bg-gray-300'}`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${rule.enforced ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </div>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">
                    Warning at (%)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      max={99}
                      value={rule.warningPercent}
                      onChange={e => updateRule(rule.id, 'warningPercent', Number(e.target.value))}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300"
                    />
                    <span className="text-xs text-amber-600 font-bold">%</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Show amber warning flag</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium block mb-1">
                    Block at (%)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      max={200}
                      value={rule.blockPercent}
                      onChange={e => updateRule(rule.id, 'blockPercent', Number(e.target.value))}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300"
                    />
                    <span className="text-xs text-red-600 font-bold">%</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Block approval if enforced</p>
                </div>
              </div>

              {/* Visual preview */}
              <div className="mt-2">
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span>0%</span>
                  <span className="text-amber-600">{rule.warningPercent}% warn</span>
                  <span className="text-red-600">{rule.blockPercent}% block</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden relative">
                  <div
                    className="absolute h-full bg-green-400 rounded-full"
                    style={{ width: `${Math.min(rule.warningPercent, 100)}%` }}
                  />
                  <div
                    className="absolute h-full bg-amber-400 rounded-full"
                    style={{ left: `${Math.min(rule.warningPercent, 100)}%`, width: `${Math.min(rule.blockPercent - rule.warningPercent, 100 - rule.warningPercent)}%` }}
                  />
                </div>
              </div>
            </div>
          ))}

          <div className="flex items-start gap-2 bg-blue-50 rounded-xl p-3">
            <Info size={13} className="text-blue-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-700">
              When <strong>Enforce</strong> is on, approvals will be blocked if the request would exceed the block threshold. When off, overages are flagged but not blocked.
            </p>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-xl hover:bg-gray-50 text-gray-600 font-semibold transition-colors">
            Cancel
          </button>
          <button onClick={() => { onSave(localRules); onClose(); }} className="px-4 py-2 text-sm bg-orange-600 text-white rounded-xl hover:bg-orange-700 font-semibold transition-colors">
            Save Rules
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Budget Bar ───────────────────────────────────────────────────────────────

function BudgetBar({
  total,
  spent,
  committed,
  requestAmount,
  warningPercent,
  blockPercent,
}: {
  total: number;
  spent: number;
  committed: number;
  requestAmount: number;
  warningPercent: number;
  blockPercent: number;
}) {
  const spentPct = Math.min((spent / total) * 100, 100);
  const committedPct = Math.min((committed / total) * 100, 100 - spentPct);
  const requestPct = Math.min((requestAmount / total) * 100, 100 - spentPct - committedPct);
  const overflowPct = Math.max(0, ((spent + committed + requestAmount) / total) * 100 - 100);

  return (
    <div className="space-y-1.5">
      <div className="h-3 bg-gray-100 rounded-full overflow-hidden flex">
        {/* Spent */}
        <div
          className="h-full bg-indigo-500 transition-all"
          style={{ width: `${spentPct}%` }}
          title={`Spent: ${formatUGX(spent)}`}
        />
        {/* Committed */}
        <div
          className="h-full bg-amber-400 transition-all"
          style={{ width: `${committedPct}%` }}
          title={`Committed: ${formatUGX(committed)}`}
        />
        {/* This request */}
        {requestAmount > 0 && (
          <div
            className={`h-full transition-all ${overflowPct > 0 ? 'bg-red-500' : 'bg-orange-400'}`}
            style={{ width: `${requestPct}%` }}
            title={`This request: ${formatUGX(requestAmount)}`}
          />
        )}
      </div>
      {/* Threshold markers */}
      <div className="relative h-1">
        <div
          className="absolute top-0 w-0.5 h-3 bg-amber-500 rounded-full -translate-y-2"
          style={{ left: `${Math.min(warningPercent, 100)}%` }}
          title={`Warning at ${warningPercent}%`}
        />
        <div
          className="absolute top-0 w-0.5 h-3 bg-red-500 rounded-full -translate-y-2"
          style={{ left: `${Math.min(blockPercent, 100)}%` }}
          title={`Block at ${blockPercent}%`}
        />
      </div>
      {/* Legend */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-sm bg-indigo-500" />
          <span className="text-xs text-gray-500">Spent</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-sm bg-amber-400" />
          <span className="text-xs text-gray-500">Committed</span>
        </div>
        {requestAmount > 0 && (
          <div className="flex items-center gap-1.5">
            <div className={`w-2.5 h-2.5 rounded-sm ${overflowPct > 0 ? 'bg-red-500' : 'bg-orange-400'}`} />
            <span className="text-xs text-gray-500">This Request</span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-sm bg-gray-100 border border-gray-300" />
          <span className="text-xs text-gray-500">Available</span>
        </div>
      </div>
    </div>
  );
}

// ─── Main BudgetTracker Component ─────────────────────────────────────────────

export default function BudgetTracker({
  budgetCode,
  requestAmount,
  currency = 'UGX',
  compact = false,
  showSettings = false,
}: BudgetTrackerProps) {
  const [rules, setRules] = useState<BudgetThresholdRule[]>(DEFAULT_RULES);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [expanded, setExpanded] = useState(!compact);

  const budget = MOCK_BUDGETS.find(b => b.budgetCode === budgetCode);

  if (!budget) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 flex items-center gap-2">
        <Info size={14} className="text-gray-400" />
        <span className="text-xs text-gray-500">Budget code <strong>{budgetCode}</strong> not found in system.</span>
      </div>
    );
  }

  const rule = rules[0];
  const available = budget.totalBudget - budget.committed - budget.spent;
  const utilisedAfterRequest = budget.committed + budget.spent + requestAmount;
  const utilisationPct = (utilisedAfterRequest / budget.totalBudget) * 100;
  const currentUtilisationPct = ((budget.committed + budget.spent) / budget.totalBudget) * 100;

  const isOverBudget = utilisedAfterRequest > budget.totalBudget;
  const isWarning = utilisationPct >= rule.warningPercent && !isOverBudget;
  const isBlocked = rule.enforced && utilisationPct >= rule.blockPercent;
  const requestExceedsAvailable = requestAmount > available;

  // Status color
  const statusColor = isOverBudget || isBlocked
    ? 'border-red-200 bg-red-50'
    : isWarning
    ? 'border-amber-200 bg-amber-50' :'border-green-200 bg-green-50';

  const headerColor = isOverBudget || isBlocked
    ? 'text-red-700'
    : isWarning
    ? 'text-amber-700' :'text-green-700';

  return (
    <>
      {showSettingsModal && (
        <ThresholdSettingsModal
          rules={rules}
          onSave={setRules}
          onClose={() => setShowSettingsModal(false)}
        />
      )}

      <div className={`rounded-xl border overflow-hidden ${statusColor}`}>
        {/* Header */}
        <div
          className={`flex items-center justify-between px-4 py-3 cursor-pointer ${compact ? 'hover:bg-black/5' : ''}`}
          onClick={() => compact && setExpanded(!expanded)}
        >
          <div className="flex items-center gap-2">
            {isOverBudget || isBlocked ? (
              <Lock size={15} className="text-red-600 flex-shrink-0" />
            ) : isWarning ? (
              <AlertTriangle size={15} className="text-amber-600 flex-shrink-0" />
            ) : (
              <TrendingUp size={15} className="text-green-600 flex-shrink-0" />
            )}
            <div>
              <div className={`text-xs font-bold ${headerColor}`}>
                {isBlocked
                  ? '⛔ Budget Limit Exceeded — Approval Blocked'
                  : isOverBudget
                  ? '🚨 Over Budget — Overage Flagged'
                  : isWarning
                  ? `⚠️ Budget Warning — ${utilisationPct.toFixed(0)}% Utilised`
                  : `✅ Budget Available — ${(100 - utilisationPct).toFixed(0)}% Remaining`}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">{budget.budgetName} · {budget.department}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {showSettings && (
              <button
                onClick={e => { e.stopPropagation(); setShowSettingsModal(true); }}
                className="p-1.5 rounded-lg hover:bg-black/10 transition-colors"
                title="Configure threshold rules"
              >
                <Settings size={13} className="text-gray-500" />
              </button>
            )}
            {compact && (
              expanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />
            )}
          </div>
        </div>

        {/* Body */}
        {expanded && (
          <div className="px-4 pb-4 space-y-4 border-t border-black/5">
            {/* KPI Row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-3">
              {[
                {
                  label: 'Total Budget',
                  value: formatUGX(budget.totalBudget),
                  sub: budget.currency,
                  color: 'text-gray-900',
                  bg: 'bg-white',
                  icon: <DollarSign size={13} className="text-gray-400" />,
                },
                {
                  label: 'Spent',
                  value: formatUGX(budget.spent),
                  sub: `${((budget.spent / budget.totalBudget) * 100).toFixed(1)}% of budget`,
                  color: 'text-indigo-700',
                  bg: 'bg-white',
                  icon: <TrendingUp size={13} className="text-indigo-400" />,
                },
                {
                  label: 'Committed',
                  value: formatUGX(budget.committed),
                  sub: `${((budget.committed / budget.totalBudget) * 100).toFixed(1)}% of budget`,
                  color: 'text-amber-700',
                  bg: 'bg-white',
                  icon: <Lock size={13} className="text-amber-400" />,
                },
                {
                  label: 'Available',
                  value: formatUGX(Math.max(available, 0)),
                  sub: available < 0 ? `Over by ${formatUGX(-available)}` : `${((Math.max(available, 0) / budget.totalBudget) * 100).toFixed(1)}% remaining`,
                  color: available < 0 ? 'text-red-700' : 'text-green-700',
                  bg: 'bg-white',
                  icon: available < 0
                    ? <AlertTriangle size={13} className="text-red-400" />
                    : <TrendingUp size={13} className="text-green-400" />,
                },
              ].map(card => (
                <div key={card.label} className={`${card.bg} rounded-lg p-2.5 border border-gray-100`}>
                  <div className="flex items-center gap-1 mb-1">
                    {card.icon}
                    <span className="text-xs text-gray-400 font-medium">{card.label}</span>
                  </div>
                  <div className={`text-sm font-bold ${card.color}`}>{card.value}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{card.sub}</div>
                </div>
              ))}
            </div>

            {/* Budget Bar */}
            <BudgetBar
              total={budget.totalBudget}
              spent={budget.spent}
              committed={budget.committed}
              requestAmount={requestAmount}
              warningPercent={rule.warningPercent}
              blockPercent={rule.blockPercent}
            />

            {/* This Request Impact */}
            {requestAmount > 0 && (
              <div className={`rounded-lg p-3 border ${requestExceedsAvailable ? 'bg-red-50 border-red-200' : 'bg-white border-gray-100'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-gray-700">This Request Impact</span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                    requestExceedsAvailable ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                  }`}>
                    {formatUGX(requestAmount)}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-400">Utilisation before: </span>
                    <span className="font-semibold text-gray-700">{currentUtilisationPct.toFixed(1)}%</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Utilisation after: </span>
                    <span className={`font-semibold ${utilisationPct > 100 ? 'text-red-600' : utilisationPct >= rule.warningPercent ? 'text-amber-600' : 'text-gray-700'}`}>
                      {utilisationPct.toFixed(1)}%
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">Available now: </span>
                    <span className="font-semibold text-gray-700">{formatUGX(available)}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Available after: </span>
                    <span className={`font-semibold ${available - requestAmount < 0 ? 'text-red-600' : 'text-gray-700'}`}>
                      {formatUGX(available - requestAmount)}
                    </span>
                  </div>
                </div>
                {requestExceedsAvailable && (
                  <div className="mt-2 flex items-center gap-1.5 text-xs text-red-700 font-semibold">
                    <AlertTriangle size={12} />
                    Overage: {formatUGX(requestAmount - available)} exceeds available budget
                  </div>
                )}
              </div>
            )}

            {/* Threshold Info */}
            <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-amber-400" />
                <span>Warning at {rule.warningPercent}%</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span>Block at {rule.blockPercent}% {rule.enforced ? '(enforced)' : '(advisory)'}</span>
              </div>
              {showSettings && (
                <button
                  onClick={() => setShowSettingsModal(true)}
                  className="ml-auto flex items-center gap-1 text-orange-600 hover:text-orange-700 font-semibold"
                >
                  <Settings size={11} /> Configure Rules
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Budget Summary Panel (for listing multiple budget lines) ─────────────────

export function BudgetSummaryPanel({
  budgetCodes,
  showSettings = false,
}: {
  budgetCodes: string[];
  showSettings?: boolean;
}) {
  const [rules, setRules] = useState<BudgetThresholdRule[]>(DEFAULT_RULES);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  const budgets = MOCK_BUDGETS.filter(b => budgetCodes.includes(b.budgetCode));
  const rule = rules[0];

  if (budgets.length === 0) return null;

  return (
    <>
      {showSettingsModal && (
        <ThresholdSettingsModal
          rules={rules}
          onSave={setRules}
          onClose={() => setShowSettingsModal(false)}
        />
      )}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <DollarSign size={15} className="text-orange-500" />
            <span className="text-sm font-bold text-gray-900">Budget Overview</span>
          </div>
          {showSettings && (
            <button
              onClick={() => setShowSettingsModal(true)}
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-orange-600 font-semibold transition-colors"
            >
              <Settings size={12} /> Threshold Rules
            </button>
          )}
        </div>
        <div className="divide-y divide-gray-50">
          {budgets.map(budget => {
            const available = budget.totalBudget - budget.committed - budget.spent;
            const utilisationPct = ((budget.committed + budget.spent) / budget.totalBudget) * 100;
            const isOver = available < 0;
            const isWarn = utilisationPct >= rule.warningPercent && !isOver;

            return (
              <div key={budget.budgetCode} className="px-4 py-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-xs font-bold text-gray-800">{budget.budgetCode}</div>
                    <div className="text-xs text-gray-400">{budget.department}</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-xs font-bold ${isOver ? 'text-red-600' : isWarn ? 'text-amber-600' : 'text-green-600'}`}>
                      {isOver ? `Over by ${formatUGX(-available)}` : `${formatUGX(available)} available`}
                    </div>
                    <div className="text-xs text-gray-400">{utilisationPct.toFixed(1)}% utilised</div>
                  </div>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex">
                  <div className="h-full bg-indigo-500" style={{ width: `${Math.min((budget.spent / budget.totalBudget) * 100, 100)}%` }} />
                  <div className="h-full bg-amber-400" style={{ width: `${Math.min((budget.committed / budget.totalBudget) * 100, 100 - (budget.spent / budget.totalBudget) * 100)}%` }} />
                </div>
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>Spent: {formatUGX(budget.spent)}</span>
                  <span>Committed: {formatUGX(budget.committed)}</span>
                  <span>Total: {formatUGX(budget.totalBudget)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
